/**
 * Resolves the connection bindings a step must carry in a target workflow context.
 *
 * Steps travel between contexts (cross-workflow paste, workflow duplication, import
 * into an integration) with whatever `credentialId` / `integrationAppId` their
 * source carried, and neither survives the move:
 *
 * - Integration workflows resolve credentials per installation — provisioning for the
 *   integration's embedded app, `integrationAppId` for connector apps. A company
 *   `credentialId` is never valid there: it binds every end user's run to the
 *   partner's own connection (or fails "No Connection Found" when it does not
 *   resolve), and the editor has no surface that shows or clears it.
 * - Workflows without an integration have no integration apps, so `integrationAppId`
 *   is always cleared; `credentialId` is left alone because company connections are
 *   the only credential source there.
 *
 * In an integration workflow a binding is kept when it belongs to the target
 * integration; otherwise the step is bound to the integration's app for its `appId`
 * when exactly one exists. No match, or several apps for the same `appId`, resolves
 * to null so the editor asks for an explicit pick instead of guessing.
 *
 * Inside an integration the connection *type* is normalized too: `options.connectionType =
 * 'environment'` makes a step take its credential from a company environment connection,
 * a concept that does not exist there (the installation selects the connection), so a
 * copied step kept running on the partner's own connection whenever no installation
 * resolved. The type is dropped and the step falls back to per-step resolution; the
 * returned `options` is only present when it changed.
 *
 * Pure. `integrationApps` are the target integration's `embedded.integration_app`
 * rows (`{ id, appId }`).
 */
const withoutEnvironmentConnectionType = (options) => {
  if (options?.connectionType !== 'environment') {
    return undefined;
  }
  return Object.fromEntries(Object.entries(options).filter(([key]) => key !== 'connectionType'));
};

export const resolveStepConnectionBindings = ({ step, integrationId, integrationApps = [] }) => {
  if (!integrationId) {
    return { credentialId: step?.credentialId ?? null, integrationAppId: null };
  }

  const options = withoutEnvironmentConnectionType(step?.options);
  const withOptions = (bindings) => (options === undefined ? bindings : { ...bindings, options });

  const currentBinding = step?.integrationAppId || null;
  if (currentBinding && integrationApps.some((app) => app?.id === currentBinding)) {
    return withOptions({ credentialId: null, integrationAppId: currentBinding });
  }

  const matchingApps = integrationApps.filter((app) => !!step?.appId && app?.appId === step.appId);

  return withOptions({
    credentialId: null,
    integrationAppId: matchingApps.length === 1 ? matchingApps[0].id : null
  });
};
